import type { CodeModule } from '../types/code.types'
import { getMockModule } from '../mocks/codeModules.mock'
import type { SystemProcedure } from '../components/editors/CodeEditor/language/systemProcedures'

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

/** Runtime cache + default for nodes without presets in mocks (otherwise CodeEditor hangs on Loading). */
const moduleStore: Record<string, CodeModule> = {}

function moduleKey(id: string): string {
  return id.startsWith('module-') ? id : `module-${id}`
}

function createBlankModule(key: string): CodeModule {
  const objectId = key.replace(/^module-/, '')
  return {
    id: key,
    objectId,
    // LavkaLang is Python-compatible — `#`, not JS `//` (legacy `// Module:`
    // stubs broke CommonModules compile with "invalid syntax" on line 1).
    code: `# Module: ${objectId}\n\n`,
  }
}

/** Always returns a module: mock (clone in store), saved draft, or blank template. */
function ensureModule(id: string): CodeModule {
  const key = moduleKey(id)
  const fromMock = getMockModule(id) ?? getMockModule(key.replace(/^module-/, ''))
  if (fromMock) {
    if (!moduleStore[key]) moduleStore[key] = { ...fromMock }
    return moduleStore[key]
  }
  if (!moduleStore[key]) moduleStore[key] = createBlankModule(key)
  return moduleStore[key]
}

/** Sync — for UI without hanging on Loading until mock delay finishes or promise rejects. */
export function getModuleSync(id: string): CodeModule {
  return ensureModule(id)
}

export const codeService = {
  // TODO: replace with API call → GET /api/modules/:id
  getModule: async (id: string): Promise<CodeModule | null> => {
    await delay(60)
    return ensureModule(id)
  },

  // TODO: replace with API call → PUT /api/modules/:id
  saveModule: async (id: string, code: string): Promise<void> => {
    await delay(100)
    const key = moduleKey(id)
    const cur = ensureModule(id)
    moduleStore[key] = { ...cur, code }
  },

  // TODO: replace with API call → GET /api/system-procedures?objectType=:type
  getSystemProcedures: async (objectType: string): Promise<SystemProcedure[]> => {
    await delay(30)
    const { SYSTEM_PROCEDURES } = await import('../components/editors/CodeEditor/language/systemProcedures')
    return SYSTEM_PROCEDURES.filter(
      (p) =>
        p.autoGeneratedFor.length === 0 || p.autoGeneratedFor.includes(objectType)
    )
  },
}
